import { ApolloProvider } from '@apollo/client/react'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import { useState } from 'react'
import { Admin, CustomRoutes, Resource } from 'react-admin'
import { Provider } from 'react-redux'
import { createBrowserRouter, Navigate, Route, RouterProvider, useParams } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import {
  authProvider as authProviderInit,
  fileStorage,
  raDataGraphql,
  StorageContext,
} from '~/helpers'
import i18n from '~/i18n'
import { Layout, Login, useAppTheme } from '~/layouts'
import {
  Account,
  CatalogPage,
  Configuration,
  Dashboard,
  FeedStatus,
  InstagramCallback,
  PhotoStudio,
  SocialPost,
} from '~/pages'
import createStore from '~/reducers'
import {
  AppointmentCreate,
  AppointmentEdit,
  AppointmentIcon,
  AppointmentList,
  AppointmentQueries,
  AttributeQueries,
  BoatCreate,
  BoatEdit,
  BoatIcon,
  BoatList,
  BoatQueries,
  BoatShow,
  CategoryCreate,
  CategoryEdit,
  CategoryIcon,
  CategoryList,
  CategoryQueries,
  CommentQueries,
  CustomerCreate,
  CustomerEdit,
  CustomerIcon,
  CustomerList,
  CustomerQueries,
  CustomerShow,
  DocumentCreate,
  DocumentEdit,
  DocumentIcon,
  DocumentList,
  DocumentQueries,
  HistoryIcon,
  HistoryList,
  HistoryQueries,
  HistoryTransactionIcon,
  HistoryTransactionList,
  HistoryTransactionQueries,
  RoleIcon,
  SectionEdit,
  SectionIcon,
  SectionList,
  SectionQueries,
  SelectionCreate,
  SelectionEdit,
  SelectionIcon,
  SelectionList,
  SelectionQueries,
  SupplierCreate,
  SupplierEdit,
  SupplierList,
  SupplierQueries,
  UserCreate,
  UserEdit,
  UserList,
  UserQueries,
} from '~/resources'
import './App.css'

const title = 'yachthandel24'

export const { dataProvider, client } = raDataGraphql({
  client: {
    graphqlURL: import.meta.env.REACT_APP_GRAPHQL_URI,
  },
  buildQuery: {
    queries: {
      history_transactions: HistoryTransactionQueries,
      history: HistoryQueries,
      customers: CustomerQueries,
      boats: BoatQueries,
      categories: CategoryQueries,
      attributes: AttributeQueries,
      users: UserQueries,
      suppliers: SupplierQueries,
      sections: SectionQueries,
      selections: SelectionQueries,
      comments: CommentQueries,
      appointments: AppointmentQueries,
      documents: DocumentQueries,
    },
    extendFields: [HistoryQueries.extendFields],
  },
})

/** Legacy /boats/guided_edit/:id links still in the wild. */
const GuidedEditRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/boats/${id}/guided`} replace />
}

/**
 * Everything below <Provider>, so the theme can come from persisted redux state.
 *
 * react-admin 4 moved `theme` from <Layout> to <Admin>. That is not just a
 * rename: an unrecognised `theme` prop reaches a styled component, and emotion
 * reuses the React props object verbatim when `theme` is already on it, so
 * MUI's attachTheme then tries to mutate frozen props and throws.
 *
 * The <BrowserRouter> is load-bearing: react-admin 5 creates a **HashRouter**
 * by default, and this app has always used browser paths (it built its own
 * history with createBrowserHistory). <AdminRouter> skips creating its own
 * router when it is already inside one, so wrapping keeps /boats working
 * instead of moving every route behind a #.
 *
 * react-admin 5's <Admin> also replaces the hand-rolled AdminContext +
 * CoreAdminRouter + <Switch> arrangement: it owns routing, the auth gate and
 * the login page itself, so AuthGate and the manual createBrowserHistory are
 * gone.
 */
const AdminWithTheme = ({ authProvider, i18nProvider }) => (
  <Admin
    title={title}
    theme={useAppTheme()}
    dataProvider={dataProvider}
    authProvider={authProvider}
    i18nProvider={i18nProvider}
    loginPage={Login}
    dashboard={Dashboard}
    layout={Layout}
    requireAuth
  >
    <CustomRoutes>
      <Route path='/configuration' element={<Configuration />} />
      <Route path='/account' element={<Account />} />
      <Route path='/photos' element={<PhotoStudio />} />
      <Route path='/feed' element={<FeedStatus />} />
      <Route path='/social' element={<SocialPost />} />
      <Route path='/catalog' element={<CatalogPage />} />
      <Route path='/instagram' element={<InstagramCallback />} />
      <Route path='/boats/guided_create' element={<Navigate to='/boats/create/guided' replace />} />
      <Route path='/boats/guided_edit/:id' element={<GuidedEditRedirect />} />
      {/* The user screens were /auth_accounts while accounts lived in HBP. */}
      <Route path='/auth_accounts/*' element={<Navigate to='/users' replace />} />
    </CustomRoutes>

    <Resource
      name='customers'
      icon={CustomerIcon}
      list={CustomerList}
      show={CustomerShow}
      edit={CustomerEdit}
      create={CustomerCreate}
    />
    <Resource
      name='suppliers'
      icon={CustomerIcon}
      create={SupplierCreate}
      list={SupplierList}
      edit={SupplierEdit}
    />
    <Resource
      name='boats'
      icon={BoatIcon}
      list={BoatList}
      show={BoatShow}
      edit={BoatEdit}
      create={BoatCreate}
    />
    <Resource name='sections' icon={SectionIcon} list={SectionList} edit={SectionEdit} />
    <Resource
      name='categories'
      icon={CategoryIcon}
      list={CategoryList}
      edit={CategoryEdit}
      create={CategoryCreate}
    />
    <Resource
      name='selections'
      icon={SelectionIcon}
      list={SelectionList}
      edit={SelectionEdit}
      create={SelectionCreate}
    />
    <Resource
      name='documents'
      icon={DocumentIcon}
      list={DocumentList}
      edit={DocumentEdit}
      create={DocumentCreate}
    />
    <Resource name='choices' />
    <Resource name='supplier_services_lkup' />
    <Resource name='emails' />
    <Resource name='phones' />
    <Resource name='comments' />
    <Resource name='attachments' />
    <Resource name='attributes' />
    <Resource
      name='appointments'
      icon={AppointmentIcon}
      list={AppointmentList}
      edit={AppointmentEdit}
      create={AppointmentCreate}
    />
    <Resource name='appointment_customer' />
    <Resource name='history' icon={HistoryIcon} list={HistoryList} />
    <Resource
      name='history_transactions'
      icon={HistoryTransactionIcon}
      list={HistoryTransactionList}
    />
    <Resource name='users' icon={RoleIcon} list={UserList} create={UserCreate} edit={UserEdit} />
  </Admin>
)

// ! important: resources and custom queries have to be added here
export const App = () => {
  // Built once. <RouterProvider> holds the element tree, so recreating the
  // router — or the store behind it — on a re-render would remount the whole
  // admin and drop its state.
  const [{ fStore, store, persistor, router }] = useState(() => {
    const authProvider = authProviderInit(client)
    // German until the layout applies the saved choice — and on the login
    // page, which has no layout: without it a new device signed in in English.
    const i18nProvider = polyglotI18nProvider((locale) => i18n[locale] || i18n['de'], 'de')

    return {
      fStore: fileStorage,
      ...createStore(),
      // A *data* router, not <BrowserRouter>: react-admin's
      // warnWhenUnsavedChanges is built on react-router's useBlocker, which
      // only exists on a data router. Without one react-admin logs
      // "Cannot use the warnWhenUnsavedChanges feature outside of a
      // DataRouter" and silently disables it on all 15 create/edit forms.
      router: createBrowserRouter([
        {
          path: '*',
          element: <AdminWithTheme authProvider={authProvider} i18nProvider={i18nProvider} />,
        },
      ]),
    }
  })

  return (
    <ApolloProvider client={client}>
      <StorageContext.Provider value={fStore}>
        <Provider store={store}>
          <PersistGate persistor={persistor}>
            <RouterProvider router={router} />
          </PersistGate>
        </Provider>
      </StorageContext.Provider>
    </ApolloProvider>
  )
}

export default App
